package org.dronda.api.wishlist

import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.serialization.Serializable

object EventLimits {
    const val MAX_PARTICIPANTS = 50
    const val MAX_SANTA_SENDS = 5
    const val MIN_SANTA_PARTICIPANTS = 3
}

@Serializable
enum class EventMode {
    STANDARD,
    SECRET_SANTA,
}

@Serializable
data class Group(
    val id: String,
    val name: String,
    val people: List<User>,
)

@Serializable
data class Event(
    val id: String,
    val name: String,
    val date: LocalDate,
    val mode: EventMode = EventMode.STANDARD,
    val budget: String? = null,
)

@Serializable
data class EventWithWishLists(
    val event: Event,
    val wishlists: List<WishList>,
    val isAdmin: Boolean = false,
    val santa: SantaViewerState? = null,
)

/**
 * Secret Santa state from the point of view of the viewer. Only ever contains the viewer's own recipient.
 */
@Serializable
data class SantaViewerState(
    val isDrawn: Boolean,
    val recipientUserId: String?,
)

@Serializable
data class CreateEventResponse(
    val eventId: String,
    val unknownEmails: List<String>,
)

@Serializable
data class EventAdminState(
    val event: Event,
    val participants: List<ParticipantStatus>,
    val maxParticipants: Int,
    val santa: SantaAdminState?,
)

@Serializable
data class ParticipantStatus(
    val user: User,
    val isAdmin: Boolean,
    val isAssigned: Boolean,
    val lastEmailedAt: LocalDateTime?,
)

@Serializable
data class SantaAdminState(
    val isDrawn: Boolean,
    val sendsUsed: Int,
    val maxSends: Int,
    val unassignedCount: Int,
    val allowedSendKinds: List<SendKind>,
    val exclusionsChanged: Boolean = false,
    val exclusions: List<ExclusionPair>,
)

@Serializable
data class ExclusionPair(
    val userIdA: String,
    val userIdB: String,
    val direction: ExclusionDirection = ExclusionDirection.BOTH,
)

@Serializable
enum class ExclusionDirection {
    BOTH,
    A_TO_B,
    B_TO_A,
}

@Serializable
enum class SendKind {
    INITIAL,
    SPLICE,
    RESHUFFLE,
}

@Serializable
data class SendAssignmentsResponse(
    val emailsSent: Int,
    val emailsFailed: Int,
    val sendsRemaining: Int,
)

@Serializable
data class ApiError(
    val code: String,
    val message: String,
)

@Serializable
data class User(
    val id: String,
    val username: String,
    val name: String
)

@Serializable
data class WishList(
    val id: String,
    val name: String,
    val user: User,
)

@Serializable
data class WishListFull(
    val id: String,
    val name: String,
    val user: User,
    val wishlistItems: List<WishlistItem>,
    val purchasedItems: List<WishlistItem>,
    val isOwner: Boolean
)

@Serializable
data class WishlistItem(
    val id: String,
    val name: String,
    val description: String?,
    val url: String?,
    val checkedUser: User?,
    val isPurchased: Boolean,
    val addedByUserId: String? = null,
)
